import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { routes } from '~/constants';
import { relationshipModel, serviceModel, UserRole } from '~/models';
import { hasClientViewPermission, RelationshipStatus } from '~/models/relationship';
import { formatDateTimeWithLongMonth } from '~/utils';
import { getTimezoneAbbreviation } from '~/utils/date-timezone.utils';
import { COBStep, getCommonReachableSteps } from './ClientOnboarding.common.utils';
export { COBStep, getStepName, PreviewState, termsOfServiceStepIndex } from './ClientOnboarding.common.utils';
export function getReachableSteps(relationship) {
    return [
        COBStep.welcome,
        ...getCommonReachableSteps(relationship),
        COBStep.allSet
    ];
}
export var Reason = function(Reason) {
    Reason[Reason["PaymentMethod"] = COBStep.paymentMethod] = "PaymentMethod";
    Reason[Reason["ReviewServices"] = COBStep.reviewServices] = "ReviewServices";
    Reason[Reason["SelectAPackage"] = COBStep.selectAPackage] = "SelectAPackage";
    Reason["ApproveTermsOfServices"] = "approveTermsOfServices";
    Reason["ReviewerApprove"] = "reviewerApprove";
    return Reason;
}({});
export const reasonToText = {
    [Reason.PaymentMethod]: 'To approve the agreement, Please add a payment method.',
    [Reason.ReviewServices]: 'To approve the agreement, It is required to select at least one service.',
    [Reason.SelectAPackage]: 'To approve the agreement, Please select a package.',
    ["reviewerApprove"]: 'You can view and interact with the proposal, but not approve it.',
    ["approveTermsOfServices"]: 'To approve the agreement, Please agree to the Terms of Service.'
};
export const getNextStepButtonText = (step, relationship)=>{
    const reachableSteps = getReachableSteps(relationship);
    const currentStepIndex = reachableSteps.indexOf(step);
    if (currentStepIndex === -1) {
        throw new Error('Unreachable step');
    }
    const nextStep = reachableSteps[currentStepIndex + 1];
    if (!nextStep) {
        throw new Error('should have next step');
    }
    if (nextStep === COBStep.selectAPackage) {
        return 'Select a Package';
    }
    if (nextStep === COBStep.paymentMethod) {
        return 'Add payment method';
    }
    switch(step){
        case COBStep.welcome:
            return relationship.isSample ? 'Review test agreement' : 'Review the agreement';
        default:
            return 'Next';
    }
};
export const getInitialSelectedBundleId = (getFromStorage, relationship, userId)=>{
    const fromStorage = getFromStorage('selected_bundle_id');
    if (fromStorage && !relationshipModel.isSignatory(relationship, userId) && !relationshipModel.isReviewer(relationship, userId)) {
        return fromStorage;
    }
    return relationshipModel.selectedBundleId(relationship) || undefined;
};
export const getInitialSelectedOptionalServices = (param)=>{
    let { setToStorage, getFromStorage, isPreview, relationship, selectedBundleID, userId } = param;
    const getInitialSelectedOptionalServices = ()=>{
        const initialSelectedOptionalServices = relationshipModel.includedByDefaultOptionalServices(relationship, selectedBundleID);
        setToStorage('selected_optional_services', initialSelectedOptionalServices);
        return initialSelectedOptionalServices;
    };
    const fromStorage = getFromStorage('selected_optional_services');
    const fromRelationship = relationshipModel.selectedOptionalServices(relationship);
    const hasSelectedOptionalServices = fromRelationship.length > 0;
    if (isPreview) {
        return getInitialSelectedOptionalServices();
    }
    if (fromStorage && !relationshipModel.isSignatory(relationship, userId) && !relationshipModel.isReviewer(relationship, userId)) {
        return fromStorage;
    }
    if (hasSelectedOptionalServices) {
        setToStorage('selected_optional_services', fromRelationship);
        return fromRelationship;
    }
    return getInitialSelectedOptionalServices();
};
export const getInitialSelectedPaymentMethod = (getFromStorage, relationship, userId)=>{
    const selectedPaymentFromStorage = getFromStorage('selected_payment_method');
    if (selectedPaymentFromStorage && !relationshipModel.isSignatory(relationship, userId) && !relationshipModel.isReviewer(relationship, userId)) {
        return selectedPaymentFromStorage;
    } else {
        return {
            fundingSourceId: relationshipModel.selectedFundingSourceId(relationship) || undefined
        };
    }
};
export function isApproveProposalDisabled(disabledReason, isPreview, relationship, userId, role) {
    if (disabledReason) {
        return true;
    }
    return relationship.isSample || isPreview ? isPreview : !getIsAllowedToSIgn({
        relationship,
        userId,
        role
    });
}
export const getPrimaryRelationshipApprovedNavigateUrl = (param)=>{
    let { relationshipId, hasEnteredWizard } = param;
    return hasEnteredWizard ? routes.allSet(relationshipId) : routes.agreement(relationshipId);
};
export const getSignatoryRelationshipApprovedNavigateUrl = (param)=>{
    let { hasEnteredWizard, role, relationshipId } = param;
    if (hasEnteredWizard) {
        return routes.allSignaturesAcquired;
    }
    if (!hasClientViewPermission(role)) {
        return routes.noPermissionsRelationshipAlreadyApproved;
    } else {
        return routes.agreement(relationshipId);
    }
};
const getUrl = (param)=>{
    let { relationshipStatus, hasStartedWithPendingStatus, isPrimary, relationshipId, role, vendorName, isVendor } = param;
    switch(relationshipStatus){
        case RelationshipStatus.Approved:
            return {
                url: isPrimary ? getPrimaryRelationshipApprovedNavigateUrl({
                    relationshipId,
                    hasEnteredWizard: hasStartedWithPendingStatus
                }) : getSignatoryRelationshipApprovedNavigateUrl({
                    relationshipId,
                    role,
                    hasEnteredWizard: hasStartedWithPendingStatus
                })
            };
        case RelationshipStatus.Terminated:
            if (hasClientViewPermission(role)) {
                return {
                    url: routes.agreement(relationshipId)
                };
            }
            return null;
        case RelationshipStatus.Canceled:
            return {
                url: routes.canceledProposalPage
            };
        case RelationshipStatus.Pending:
            return null;
        case RelationshipStatus.Expired:
            {
                if (isVendor) return null;
                const state = {
                    vendorName
                };
                return {
                    url: routes.expiredProposalPage,
                    state
                };
            }
    }
};
export function clientOnboardingNavigateStateReducer(state, action) {
    const newState = _object_spread({}, state);
    switch(action.type){
        case 'RelationshipStatusChanged':
            newState.relationshipStatus = action.status;
            break;
        default:
            return state;
    }
    const value = getUrl({
        relationshipStatus: newState.relationshipStatus,
        hasStartedWithPendingStatus: newState.hasStartedWithPendingStatus,
        isPrimary: newState.isPrimary,
        relationshipId: newState.relationshipId,
        role: newState.role,
        vendorName: newState.vendorName,
        isVendor: newState.isVendor
    });
    newState.urlToNavigate = (value === null || value === void 0 ? void 0 : value.url) || null;
    newState.state = value === null || value === void 0 ? void 0 : value.state;
    return newState;
}
export function isAllowedToGoBack(url) {
    return url === routes.canceledProposalPage;
}
export function getIsAllowedToSIgn(param) {
    let { relationship, userId, role } = param;
    if (relationshipModel.hasSignatories(relationship)) {
        return relationshipModel.isSignatory(relationship, userId) || relationshipModel.isBuyer(relationship, userId);
    } else {
        return relationshipModel.isBuyer(relationship, userId) || role === UserRole.Owner;
    }
}
export function getExpirationDateMessage(expirationDate) {
    if (!expirationDate) return null;
    //im adding +1 to the day because the offer expired at the END of the chosen expiration date
    //if i won't add +1, resetting the time to midnight will be the biginning of this day
    const utcMidnight = new Date(Date.UTC(expirationDate.year, expirationDate.month - 1, expirationDate.day + 1, 0, 0, 0, 0));
    const timezoneAbbreviation = getTimezoneAbbreviation(utcMidnight);
    return "Discounted rates apply if this proposal is approved by ".concat(formatDateTimeWithLongMonth(utcMidnight), " (").concat(timezoneAbbreviation || 'your timezone', ").");
}
export function computeApproveDisabledReason(relationship, reachableSteps, selectedPayment, selectedBundleID, selectedOptionalServices, termsOfServiceStatements) {
    if (!relationship.isSample && reachableSteps.includes(COBStep.paymentMethod) && !(selectedPayment === null || selectedPayment === void 0 ? void 0 : selectedPayment.fundingSourceId)) {
        return Reason.PaymentMethod;
    }
    if (reachableSteps.includes(COBStep.selectAPackage) && !selectedBundleID) {
        return Reason.SelectAPackage;
    }
    if (termsOfServiceStatements.includes(false)) {
        return "approveTermsOfServices";
    }
    if (!relationshipModel.actualServices(relationship, selectedBundleID).some((service)=>serviceModel.isMandatory(service.participation) || selectedOptionalServices.includes(service.id))) {
        return Reason.ReviewServices;
    }
    return undefined;
}
